/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ThemeProvider } from "@emotion/react";
import { Button } from "@mui/material";
import MUIDataTable from "mui-datatables";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import { getData } from "../../Services/getData";
import { localUrlEnum, urlEnum } from "../../Utils/urlEnum";
import { Vocabulary } from "../../Utils/Vocabulary";
import { getMuiTheme } from "../../Utils/globalStyleTables";
import { tableLayoutOnMobile } from "../../Utils/Constants";
import formatDate, { europeanDate } from "../../Utils/formatDate";
import { isMobile } from "../../Utils/utils";
import DocumentsFilters, { DocumentFilters } from "./DocumentsFilters";
import withRole from "../../Utils/withRole";
import AddDocumentModal from "./AddDocumentModal";
import FilePreview from "../../Utils/FilePreview";

export type DocumentsState = {
  documents: Array<DocumentData>;
  documentsNumber: number;
  documentTypes?: string[];
};

export type DocumentData = {
  id?: any;
  created_at?: string;
  updated_at?: string;
  name: string;
  type: string;
  size: number;
  file_path: string;
  description?: string;
  user_r?: any;
  vehicle_id?: number;
};

function DocumentsTable() {
  const location = useLocation();
  const navigate = useNavigate();

  const [state, setState] = useState<DocumentsState>({
    documents: new Array<DocumentData>(),
    documentsNumber: 0,
    documentTypes: [],
  });

  const [filters, setFilters] = useState<DocumentFilters>({
    status: 0,
    page: 0,
    perPage: 10,
    search: null,
    startDate: null,
    endDate: null,
    documentType: null,
  });

  const [openAddModal, setOpenAddModal] = useState(false);

  useEffect(() => {
    getDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  /**
   * Updates the filters from URL and fetches the documents from API using the new filters.
   * The filters are extracted from the URL search parameters and updated in the component state.
   * Then the documents are fetched from API with the new filters and the state is updated.
   */
  const getDocuments = () => {
    const searchParams = new URLSearchParams(location.search);
    const newFilters: DocumentFilters = {
      status: parseInt(searchParams.get("status") || "0"),
      page: parseInt(searchParams.get("page") || "1") - 1,
      perPage: parseInt(searchParams.get("perPage") || "10"),
      search: searchParams.get("search"),
      startDate: searchParams.get("startDate"),
      endDate: searchParams.get("endDate"),
      documentType: searchParams.get("documentType"),
    };

    setFilters(newFilters);
    fetchDocuments(newFilters);
  };

  /**
   * Fetches documents from API and updates state
   * @param currentFilters the current filters used to fetch documents
   */
  const fetchDocuments = (currentFilters: DocumentFilters) => {
    const queryParams = new URLSearchParams();
    queryParams.append("status", currentFilters.status.toString());
    queryParams.append("page", currentFilters.page.toString());
    queryParams.append("perPage", currentFilters.perPage.toString());

    const optionalFilters = {
      search: currentFilters.search,
      startDate: currentFilters.startDate,
      endDate: currentFilters.endDate,
      documentType: currentFilters.documentType,
    };

    Object.entries(optionalFilters).forEach(([key, value]) => {
      if (value) queryParams.append(key, value);
    });

    const url = `${urlEnum.documents}?${queryParams.toString()}`;

    getData(url).then((res) => {
      setState({
        documents: res.data.documents || [],
        documentsNumber: res.data.documentsNumber || 0,
        documentTypes: res.data.documentTypes || [],
      });
    });
  };

  /**
   * Handles a change in any of the filters and updates the URL accordingly
   * @param filterName the name of the filter that changed
   * @param value the new value of the filter
   */
  const handleFilterChange = (filterName: string, value: any) => {
    const newFilters = { ...filters, [filterName]: value, page: 0 };
    updateFiltersAndNavigate(newFilters);
  };

  /**
   * Resets the filters to their default values (search, startDate, endDate, documentType = null, page = 0)
   * and navigates to the new URL with the updated filters
   */
  const handleResetFilters = () => {
    const resetFilters = {
      ...filters,
      search: null,
      startDate: null,
      endDate: null,
      documentType: null,
      page: 0,
    };
    updateFiltersAndNavigate(resetFilters);
  };

  /**
   * Update the URL params with the new filters and navigate to the new URL
   * @param newFilters the new filters to apply
   */
  const updateFiltersAndNavigate = (newFilters: DocumentFilters) => {
    const params = new URLSearchParams();

    params.append("status", newFilters.status.toString());
    params.append("page", (newFilters.page + 1).toString());
    params.append("perPage", newFilters.perPage.toString());

    const optionalFilters = {
      search: newFilters.search,
      startDate: newFilters.startDate,
      endDate: newFilters.endDate,
      documentType: newFilters.documentType,
    };

    Object.entries(optionalFilters).forEach(([key, value]) => {
      if (value) params.append(key, value);
    });

    navigate(`${localUrlEnum.documents}?${params.toString()}`);
  };

  /**
   * Table columns
   */
  const columns = [
    {
      name: "id",
      label: "ID",
      options: {
        filter: false,
        sort: false,
        display: false,
        download: false,
      },
    },
    {
      name: "name",
      label: Vocabulary.documentName,
      options: {
        filter: true,
        sort: false,
      },
    },
    {
      name: "type",
      label: Vocabulary.type,
      options: {
        filter: true,
        sort: false,
      },
    },
    {
      name: "descriere_financiar",
      label: Vocabulary.description,
      options: {
        filter: true,
        sort: false,
        customBodyRender: (value: any) => value || "-",
      },
    },
    {
      name: "user_r",
      label: Vocabulary.user,
      options: {
        filter: true,
        sort: false,
        customBodyRender: (user_r: any) => user_r?.name || "-",
      },
    },
    {
      name: "date",
      label: Vocabulary.date,
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value: any) => formatDate(value, europeanDate),
      },
    },
    {
      name: "file",
      label: " ",
      options: {
        filter: true,
        sort: false,
        display: true,
        customBodyRender: (file: any) => {
          if (!file) return null;
          const path = `/api/${file}`;
          return (
            <Button
              style={{ float: "right", backgroundColor: "#FF6633" }}
              size="small"
              variant="contained"
              data-file={path}
            >
              {Vocabulary.open}
            </Button>
          );
        },
      },
    },
  ];

  const options = {
    filter: false,
    download: false,
    viewColumns: false,
    responsive: tableLayoutOnMobile,
    rowsPerPageOptions: [10, 25, 50, 100],
    sort: true,
    selectableRows: "none" as any,
    print: false,
    page: filters.page,
    serverSide: true,
    rowsPerPage: filters.perPage,
    search: true,
    count: state.documentsNumber,
    textLabels: {
      body: {
        noMatch: Vocabulary.noResultsFound,
      },
      pagination: Vocabulary.pagination,
      toolbar: {
        search: Vocabulary.search,
        downloadCsv: Vocabulary.downloadExcel,
        viewColumns: Vocabulary.selectColumns,
      },
    },
    onChangePage: (page: number) => {
      const newFilters = { ...filters, page };
      updateFiltersAndNavigate(newFilters);
    },
    onChangeRowsPerPage: (numberOfRows: number) => {
      const newFilters = { ...filters, perPage: numberOfRows, page: 0 };
      updateFiltersAndNavigate(newFilters);
    },
    onSearchChange: (searchText: string | null) => {
      const newFilters = { ...filters, search: searchText, page: 0 };
      updateFiltersAndNavigate(newFilters);
    },
  };

  return (
    <div style={{ padding: isMobile() ? 10 : 20 }}>
      <ThemeProvider theme={getMuiTheme()}>
        <DocumentsFilters
          filters={filters}
          onFilterChange={handleFilterChange}
          onResetFilters={handleResetFilters}
          documentTypes={state.documentTypes || []}
        />

        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="contained"
            startIcon={<AddCircleIcon />}
            color="primary"
            onClick={() => setOpenAddModal(true)}
            style={{
              padding: "10px 15px 10px 15px",
              fontWeight: 600,
              borderRadius: "10px 10px 0px 0px",
            }}
          >
            {Vocabulary.generateDocument}
          </Button>
        </div>
        <MUIDataTable
          title=""
          data={state.documents}
          columns={columns}
          options={options}
        />

        <AddDocumentModal
          open={openAddModal}
          onClose={() => {
            setOpenAddModal(false);

            //refresh data
            const newFilters = { ...filters, page: 0 };
            updateFiltersAndNavigate(newFilters);
          }}
        />
        <FilePreview />
      </ThemeProvider>
    </div>
  );
}

export default withRole(DocumentsTable);
